import React, { Component, ReactNode } from 'react';
import { Sticky as ReactSticky } from 'react-sticky';

import styled, { css } from 'styled-components';

export { StickyContainer } from 'react-sticky';

type StickyElementProps = {
  style?: any;
  sticked: boolean;
  background: string;
};

export const StickyElement = styled.div<StickyElementProps>`
  position: relative;
  z-index: 10;
  background: transparent;
  transition: 0.2s ease;

  ${props => props.sticked &&
    css`
      background: ${props.background};

      :after {
        position: absolute;
        bottom: -7px;
        display: block;
        width: 90%;
        height: 7px;
        background: radial-gradient(ellipse farthest-side at 50% 0, rgba(0, 0, 0, 0.2) 0, rgba(0, 0, 0, 0) 100%);
        content: '';
        text-align: center;
      }
    `}
`;

type Props = {
  background: string;
  children: ReactNode;
  disabled?: boolean;
  hideOnScrollUp?: boolean;
  offsetBottom?: number;
  offsetTop?: number;
  relative?: boolean;
  relativePlaceholder: string | null;
  onStickinessChanged?: (isSticky: boolean, wasSticky: boolean) => void;
};
type State = {
  scrolling: boolean;
  direction: 'bottom' | 'top';
  lastPos: number;
  scrollStatus: boolean;
};

export class Sticky extends Component<Props, State> {
  public static displayName = 'Sticky';

  public static defaultProps: Partial<Props> = {
    background: '#fff',
    disabled: false,
    hideOnScrollUp: false,
    offsetBottom: undefined,
    offsetTop: 0,
    relative: false,
    relativePlaceholder: null,
    onStickinessChanged: () => undefined
  };

  private STICKY_SHADOW = 7;

  private STICKY_TIMEOUT = 400;

  private timeout: number | null;

  public constructor(props: Props) {
    super(props);

    this.state = {
      scrolling: false,
      direction: 'bottom',
      lastPos: 0,
      scrollStatus: false
    };

    this.timeout = null;
    this.handleScroll = this.handleScroll.bind(this);
  }

  public componentDidMount() {
    if (typeof window !== 'undefined' && this.props.hideOnScrollUp) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      window.addEventListener('scroll', this.handleScroll);
    }
  }

  public componentWillUnmount() {
    if (typeof window !== 'undefined' && this.props.hideOnScrollUp) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  public handleScroll(event: Event) {
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }

    this.timeout = window.setTimeout(() => {
      this.timeout = null;
      this.setState({ scrolling: false });
    }, this.STICKY_TIMEOUT);

    if (!this.state.scrollStatus) {
      this.setState({ scrolling: true });
    }

    if (this.state.lastPos > (event.currentTarget as HTMLElement).scrollTop) {
      this.setState({
        direction: 'top',
        lastPos: (event.currentTarget as HTMLElement).scrollTop
      });
    } else if (this.state.lastPos < (event.currentTarget as HTMLElement).scrollTop) {
      this.setState({
        direction: 'bottom',
        lastPos: (event.currentTarget as HTMLElement).scrollTop
      });
    }
  }

  public render() {
    return (
      <ReactSticky
        relative={this.props.relative}
        disableCompensation={this.props.disabled}
        topOffset={-this.props.offsetTop!}
        bottomOffset={typeof this.props.offsetBottom === 'undefined' ? this.props.offsetTop : this.props.offsetBottom}
      >
        {({
          style, isSticky, wasSticky, calculatedHeight
        }) => {
          const { props, state } = this;

          if (typeof document !== 'undefined' && props.relative && props.relativePlaceholder && style.position === 'fixed') {
            const el = document.querySelector(props.relativePlaceholder);
            style.top = el!.getBoundingClientRect().top;
          }

          if (!props.disabled && isSticky !== wasSticky) {
            props.onStickinessChanged!(isSticky, wasSticky);
          }

          if (!props.disabled && style.position === 'fixed') {
            if (style.top && style.top <= 0) {
              (style.top as number) += props.offsetTop!;
            }
          }

          if (this.props.hideOnScrollUp) {
            if (state.direction === 'top' && isSticky) {
              style.top = -(calculatedHeight + this.STICKY_SHADOW);
            }

            if (state.direction === 'top' && !state.scrolling) {
              style.top = 0;
            }
          }

          return (
            <StickyElement
              style={props.disabled ? {} : { ...style }}
              sticked={!props.disabled && style.position === 'fixed'}
              background={props.background}
            >
              {props.children}
            </StickyElement>
          );
        }}
      </ReactSticky>
    );
  }
}

/** @component */
export default Sticky;
